/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useState, useRef, useEffect, useMemo } from "react";
import "./NumberSelector.css";

interface NumberSelectorProps {
  values?: Array<number | string>;
  defaultValue?: number;
  onChange?: (value: number | string, index: number) => void;
  activeColor?: string;
  inactiveColor?: string;
  borderColor?: string;
  textColor?: string;
  subtitle?: string;
  activeTextColor?: string;
  size?: number;
  gap?: number;
  scrollable?: boolean;
  position?: "center" | "left" | "right";
  labelText?: string;
}

/**
 * NumberSelector - A customizable, scrollable number selection component
 */
const NumberSelector: React.FC<NumberSelectorProps> = ({
  values = [1, 2, 3, 4, 5],
  defaultValue = 0,
  onChange = () => {},
  activeColor = "#05acd9",
  inactiveColor = "#FFFFFF",
  borderColor = "#05ACD9",
  textColor = "#0778CF",
  activeTextColor = "#FFFFFF",
  subtitle,
  size = 25,
  gap = 5,
  scrollable = true,
  position = "center",
  labelText,
}) => {
  // Initialize selectedIndex properly
  const getInitialIndex = () => {
    const index = values.findIndex((val) => val === defaultValue);
    return index >= 0 ? index : 0;
  };

  const [selectedIndex, setSelectedIndex] = useState(getInitialIndex);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
    onChange(values[index], index);
  };

  // Update selectedIndex when defaultValue changes
  useEffect(() => {
    const index = values.findIndex((val) => val === defaultValue);
    if (index >= 0) {
      setSelectedIndex(index);
    }
  }, [defaultValue, values]);

  useEffect(() => {
    if (containerRef.current && scrollable) {
      const container = containerRef.current;
      const buttonWidth = size + gap;
      const selectedPos = buttonWidth * selectedIndex;
      const containerWidth = container.clientWidth;
      const scrollPos = selectedPos - containerWidth / 2 + size / 2;

      container.scrollTo({
        left: Math.max(0, scrollPos),
        behavior: "smooth",
      });
    }
  }, [selectedIndex, size, gap, scrollable]);

  // Calculate container styles based on props
  const containerStyle = useMemo(() => {
    const style: React.CSSProperties = {
      gap: `${gap}px`,
      // height: `${size}px`,
    };

    if (position === "center") {
      style.justifyContent = "center";
    } else if (position === "left") {
      style.justifyContent = "flex-start";
    } else if (position === "right") {
      style.justifyContent = "flex-end";
    }

    return style;
  }, [gap, size, position]);

  return (
    <div
      className="number-selector-wrapper"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: "100%",
      }}
    >
      {subtitle && (
        <p
          className="text-l mb-1"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: "20px",
            width: "auto",
            margin: "0 auto",
            fontSize: "clamp(1rem, 2vw, 1.5rem)",
          }}
        >
          {subtitle}
        </p>
      )}
      {labelText && (
        <label
          className="number-selector-label"
          style={{
            fontSize: "clamp(0.9rem, 1.5vw, 1.2rem)",
            marginBottom: "8px",
          }}
        >
          {labelText}
        </label>
      )}

      <div
        ref={containerRef}
        className={`number-selector-container ${
          scrollable ? "scrollable" : ""
        }`}
        style={{
          ...containerStyle,
          width: "100%",
          overflowX: scrollable ? "auto" : "visible",
          padding: "4px 0",
        }}
      >
        {values.map((value, index) => (
          <button
            key={index}
            onClick={() => handleSelect(index)}
            className={`number-selector-button ${
              selectedIndex === index ? "selected" : ""
            }`}
            style={{
              width: `clamp(${size}px, 5vw, ${size * 1.5}px)`,
              height: `clamp(${size}px, 5vw, ${size * 1.5}px)`,
              backgroundColor:
                selectedIndex === index ? activeColor : inactiveColor,
              border: `1px solid ${borderColor}`,
              color: selectedIndex === index ? activeTextColor : textColor,
              fontSize: "clamp(1rem, 2vw, 1.5rem)",
              minWidth: "40px",
              minHeight: "40px",
              maxWidth: "60px",
              maxHeight: "60px",
              flex: "0 0 auto",
            }}
            aria-pressed={selectedIndex === index}
          >
            {value}
          </button>
        ))}
      </div>
    </div>
  );
};

export default NumberSelector;
